import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Check } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/swallacemeeting/bizdevconsult';

const trustBadges = [
  'Real results, guaranteed',
  'Results in 30 days',
  'Built by practitioners',
];

const TYPED_TEXT = 'We just get you customers.';

const TypewriterText = () => {
  const [charCount, setCharCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const started = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          runLoop();
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const runLoop = () => {
    let i = 0;
    let deleting = false;

    const tick = () => {
      if (!deleting) {
        i++;
        setCharCount(i);
        setIsDeleting(false);
        if (i >= TYPED_TEXT.length) {
          setTimeout(() => { deleting = true; tick(); }, 2000);
          return;
        }
        setTimeout(tick, 50);
      } else {
        i--;
        setCharCount(i);
        setIsDeleting(true);
        if (i <= 0) {
          setTimeout(() => { deleting = false; tick(); }, 500);
          return;
        }
        setTimeout(tick, 30);
      }
    };
    tick();
  };

  return (
    <span ref={ref} className="relative inline" aria-label={TYPED_TEXT}>
      <span
        className="relative px-3 py-1"
        style={{
          background: charCount > 0 ? 'linear-gradient(135deg, #0ea5e9, #3b82f6)' : 'transparent',
          color: '#fff',
          borderRadius: '0.25rem',
          WebkitBoxDecorationBreak: 'clone',
          boxDecorationBreak: 'clone',
          lineHeight: '1.3',
        }}
      >
        {TYPED_TEXT.slice(0, charCount)}
        <span
          className="inline-block w-[3px] ml-0.5 animate-pulse"
          style={{
            height: '1em',
            backgroundColor: charCount > 0 ? '#fff' : 'var(--brand-primary)',
            verticalAlign: 'text-bottom',
          }}
        />
      </span>
    </span>
  );
};

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="section flex items-center"
      style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', minHeight: '90vh', paddingTop: '7rem' }}
    >
      <div className="container">
        <div className="max-w-3xl">
          <p
            className="text-sm font-semibold tracking-widest uppercase mb-6"
            style={{ color: 'var(--brand-primary)' }}
          >
            B2B Pipeline Development
          </p>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-8"
            style={{ color: 'var(--text-primary)', lineHeight: '1.4', whiteSpace: 'nowrap' }}
          >
            You've tried everything.
            <br />
            <TypewriterText />
          </h1>

          <p className="text-base md:text-lg mb-10" style={{ color: 'var(--text-secondary)', maxWidth: '600px' }}>
            Outbound systems and referral networks that put real money in your bank account.
            No dashboards. No vanity metrics. Just pipeline and revenue.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-lg px-8 py-4"
              data-testid="hero-cta-primary"
              aria-label="Book a strategy call"
            >
              Book a Strategy Call
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
            <a
              href="#how-it-works"
              className="btn-secondary text-lg px-8 py-4"
              data-testid="hero-cta-secondary"
              aria-label="See how it works"
            >
              See How It Works
            </a>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            {trustBadges.map((badge) => (
              <div
                key={badge}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-gray-100 shadow-sm"
              >
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--brand-success)' }} />
                <span className="text-sm font-medium text-gray-600">{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
